import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { DemoDataService } from './demo-data.service';

export interface ScheduledMaintenance {
  scheduledDate: string; // ISO date string (YYYY-MM-DD)
  scheduledTime: string; // HH:MM format
  startTime: string; // ISO string for start time
  endTime: string; // ISO string for end time
  duration: number; // in minutes
  expectedIssues: string[]; // array of expected issues/troubles
  message?: string; // optional formatted message (generated on frontend)
}

@Injectable({
  providedIn: 'root'
})
export class MaintenanceNotificationService {
  private maintenanceInfoSubject: BehaviorSubject<ScheduledMaintenance | null> = 
    new BehaviorSubject<ScheduledMaintenance | null>(null);
  
  public maintenanceInfo$ = this.maintenanceInfoSubject.asObservable();

  constructor(private demoDataService: DemoDataService) {}

  /**
   * Fetch scheduled maintenance information from backend
   * Expected endpoint: GET /api/maintenance/scheduled
   * Expected response: {
   *   scheduledDate: "2025-11-18",
   *   scheduledTime: "02:00",
   *   duration: 30,
   *   expectedIssues: ["Service unavailable", "API downtime"],
   *   message: "Scheduled maintenance on 11-18-2025 02:00, expected issues like service unavailable and API downtime for 30 min"
   * }
   */
  /**
   * Fetch scheduled maintenance - DEMO MODE
   * Uses DemoDataService instead of HTTP call
   */
  fetchScheduledMaintenance(): Observable<ScheduledMaintenance | null> {
    return this.demoDataService.getScheduledMaintenance().pipe(
      map((response: any) => {
        if (response && response.status && response.results) {
          const maintenance = response.results;
          
          // Check if maintenance is scheduled within the next 7 days
          if (this.isWithinNextWeek(maintenance.scheduledDate, maintenance.scheduledTime)) {
            const scheduledMaintenance: ScheduledMaintenance = {
              scheduledDate: maintenance.scheduledDate,
              scheduledTime: maintenance.scheduledTime,
              startTime: maintenance.startTime,
              endTime: maintenance.endTime,
              duration: maintenance.duration || 30,
              expectedIssues: maintenance.expectedIssues || [],
              message: this.generateMessage(maintenance)
            };
            
            this.maintenanceInfoSubject.next(scheduledMaintenance);
            return scheduledMaintenance;
          }
        }
        this.maintenanceInfoSubject.next(null);
        return null;
      }),
      catchError((error) => {
        // Silently fail - don't show notification if backend doesn't support this endpoint yet
        console.log('[DEMO] Scheduled maintenance endpoint not available or error:', error);
        this.maintenanceInfoSubject.next(null);
        return of(null);
      })
    );
  }

  /**
   * Generate a formatted message from maintenance data
   * Format: "The platform will be unavailable on [Date] from [Start Time IN USER LOCAL TIME] to [End Time IN USER LOCAL TIME] due to planned maintenance."
   */
  private generateMessage(maintenance: any): string {
    if (!maintenance.startTime || !maintenance.endTime) {
      return 'Scheduled maintenance upcoming';
    }

    // Parse ISO strings and convert to user's local time
    const startDate = new Date(maintenance.startTime);
    const endDate = new Date(maintenance.endTime);

    // Format date in user's local timezone (e.g., "January 15, 2025")
    const formattedDate = this.formatDateLocal(startDate);
    
    // Format times in user's local timezone (e.g., "2:00 PM")
    const formattedStartTime = this.formatTimeLocal(startDate);
    const formattedEndTime = this.formatTimeLocal(endDate);

    return `The platform will be unavailable on ${formattedDate} from ${formattedStartTime} to ${formattedEndTime} due to planned maintenance.`;
  }

  /**
   * Format date in user's local timezone (e.g., "January 15, 2025")
   */
  private formatDateLocal(date: Date): string {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * Format time in user's local timezone (e.g., "2:00 PM")
   */
  private formatTimeLocal(date: Date): string {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  /**
   * Check if scheduled maintenance is within the next 7 days
   */
  private isWithinNextWeek(scheduledDate: string, scheduledTime: string): boolean {
    try {
      const scheduled = new Date(`${scheduledDate}T${scheduledTime}`);
      const now = new Date();
      const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      
      return scheduled >= now && scheduled <= sevenDaysFromNow;
    } catch (error) {
      console.error('Error parsing scheduled maintenance date:', error);
      return false;
    }
  }

  /**
   * Get current maintenance info (synchronous)
   */
  getCurrentMaintenanceInfo(): ScheduledMaintenance | null {
    return this.maintenanceInfoSubject.getValue();
  }
}

